import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  MarketRateCheck,
  MarketRateDialog,
  useSuggestedPrice,
} from '#/features/market-rate'
import { PaymentEditForm } from '#/features/payment-edit'
import type { PaymentFormData } from '#/features/payment-edit'
import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import { formatUsd } from '#/shared/lib/format'
import { Button } from '#/shared/ui/button'
import { PlusIcon } from '#/shared/ui/icons'
import { PriceCheckBadge } from '#/shared/ui/price-check-badge'
import { paymentToFormDefaults } from '#/widgets/payment-card'
import { SectionEditButton } from '#/widgets/shipment-card'

import { SidebarSection } from './sidebar-section'

const EMPTY_PRICE_DEFAULTS: PaymentFormData = {
  totalTariff: '',
  deposit: '',
  carrierFee: '',
  paymentMethod: '',
  paymentTerms: '',
  balanceMethod: '',
  balanceTime: '',
  balanceTermBegins: '',
  paymentNote: '',
}

function toNumber(raw: number | string | null | undefined): number | undefined {
  if (raw == null || raw === '') return undefined
  const num = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(num) ? num : undefined
}

function PaymentBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-1 flex-col gap-1 rounded-md bg-gray-100 p-1.5">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-2xl text-gray-900">{value}</span>
    </div>
  )
}

interface SidebarPaymentSectionProps {
  shipment: ShipmentDetail
  onSave: (data: PaymentFormData) => Promise<void>
  priceMode?: boolean
}

function PriceEmptyState({ onAdd }: { onAdd: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center justify-between gap-2.5 px-2.5 py-1">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold text-gray-900">
          {t('orderDetails.payment.noPriceYet')}
        </span>
        <span className="text-xs text-gray-500">
          {t('orderDetails.payment.noPriceDescription')}
        </span>
      </div>
      <Button
        type="button"
        variant="warning"
        size="xs"
        pressEffect
        className="px-3.5"
        onClick={onAdd}
      >
        <PlusIcon className="size-3.5" />
        {t('orderDetails.payment.addPrice')}
      </Button>
    </div>
  )
}

function PaymentBoxes({
  shipment,
  carrierPay,
  onEdit,
}: {
  shipment: ShipmentDetail
  carrierPay: number | undefined
  onEdit: () => void
}) {
  const { t } = useTranslation()
  const money = (raw: number | string | null | undefined) =>
    formatUsd(toNumber(raw), { showCents: true })
  return (
    <div className="group/section relative -mx-2.5 flex items-stretch gap-2.5 rounded-md px-2.5 py-1 hover:bg-gray-50">
      <SectionEditButton
        label={t('orderDetails.payment.title')}
        onClick={onEdit}
      />
      <PaymentBox
        label={t('orderDetails.payment.fields.tariff')}
        value={money(shipment.tariff)}
      />
      <PaymentBox
        label={t('orderDetails.payment.fields.deposit')}
        value={money(shipment.deposit)}
      />
      {carrierPay !== undefined && (
        <PaymentBox
          label={t('orderDetails.payment.fields.carrierPay')}
          value={money(carrierPay)}
        />
      )}
    </div>
  )
}

function PaymentSectionEdit({
  shipment,
  priceMode,
  onSubmit,
  onCancel,
}: {
  shipment: ShipmentDetail
  priceMode: boolean
  onSubmit: (data: PaymentFormData) => Promise<void>
  onCancel: () => void
}) {
  return (
    <PaymentEditForm
      shipmentId={shipment.id}
      defaultValues={
        priceMode ? EMPTY_PRICE_DEFAULTS : paymentToFormDefaults(shipment)
      }
      showCarrierDetails={!priceMode}
      priceMode={priceMode}
      headerVariant="section"
      plainInputs
      titleExtra={<MarketRateCheck shipmentId={shipment.id} />}
      priceCheckOnRight
      className="border-0 rounded-lg bg-gray-100 px-2.5 pt-0 pb-2.5"
      contentClassName="px-0"
      actionsAtBottom
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  )
}

export function SidebarPaymentSection({
  shipment,
  onSave,
  priceMode = false,
}: SidebarPaymentSectionProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [marketRateOpen, setMarketRateOpen] = useState(false)
  const suggestion = useSuggestedPrice({
    shipmentId: shipment.id,
    enabled: marketRateOpen,
  })
  const carrierPay = toNumber(shipment.carrier_pay)

  const handleSubmit = async (data: PaymentFormData) => {
    await onSave(data)
    setEditing(false)
  }

  if (editing) {
    return (
      <PaymentSectionEdit
        shipment={shipment}
        priceMode={priceMode}
        onSubmit={handleSubmit}
        onCancel={() => setEditing(false)}
      />
    )
  }

  return (
    <SidebarSection
      inset
      label={
        priceMode
          ? t('orderDetails.payment.priceTitle')
          : t('orderDetails.payment.title')
      }
      labelAction={<MarketRateCheck shipmentId={shipment.id} />}
      action={
        <PriceCheckBadge
          label={t('orderDetails.payment.samplePrice')}
          onClick={() => setMarketRateOpen(true)}
        />
      }
    >
      {priceMode ? (
        <PriceEmptyState onAdd={() => setEditing(true)} />
      ) : (
        <PaymentBoxes
          shipment={shipment}
          carrierPay={carrierPay}
          onEdit={() => setEditing(true)}
        />
      )}
      <MarketRateDialog
        open={marketRateOpen}
        onOpenChange={setMarketRateOpen}
        stats={suggestion.stats}
        rows={suggestion.rows}
        isLoading={suggestion.isLoading}
        errorMessage={suggestion.errorMessage}
      />
    </SidebarSection>
  )
}
